import { useEffect, useMemo, useState, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { Badge, Button, FormField } from "@/design-system/components/ui";
import { Textarea } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { cn } from "@/design-system/lib/utils";
import {
  BrokeragePicker,
  type BrokeragePickerResult,
} from "@/components/brokerages/BrokeragePicker";
import type {
  BrokerConversionRequest,
  BrokerConversionResultDto,
  BrokerType,
} from "@/api/brokers";
import {
  useBrokerOutstandingDeals,
  useConvertBroker,
} from "@/hooks/brokers/useBrokerConversion";

// Broker money core — the ONE shared Independent↔Junior conversion component.
// Both entry points (the broker record's "Link to brokerage" and the brokerage
// roster's "Add affiliated partner") drive this so they can never drift. It
// captures how the broker is paid, the covering firm, and — for a Junior whose
// outstanding deals would change payee — a per-deal absorb decision gated by a
// written-authorization attestation. On confirm it POSTs /brokers/{id}/convert.

type PaidAs = "Junior" | "Independent";

const paidToType: Record<PaidAs, BrokerType> = {
  Junior: "Junior",
  Independent: "Independent",
};

interface BrokerConversionModalProps {
  brokerId: string;
  /** Display name for the owner-approved copy ({Broker}). */
  brokerName: string;
  /** When set (the brokerage-roster entry point), the covering firm is fixed to
   *  the current brokerage — the picker is replaced by a static display. */
  fixedBrokerage?: { id: string; name: string } | null;
  /** The broker's current payee posture, used to preselect the chooser. */
  currentType?: BrokerType;
  onClose: () => void;
  onConverted: (result: BrokerConversionResultDto) => void;
}

export function BrokerConversionModal({
  brokerId,
  brokerName,
  fixedBrokerage = null,
  currentType,
  onClose,
  onConverted,
}: BrokerConversionModalProps) {
  const [paid, setPaid] = useState<PaidAs>(
    currentType === "Junior" ? "Junior" : "Independent",
  );
  const [firm, setFirm] = useState<BrokeragePickerResult | null>(
    fixedBrokerage
      ? { brokerageId: fixedBrokerage.id, brokerageName: fixedBrokerage.name }
      : null,
  );
  const [decisions, setDecisions] = useState<Record<string, boolean>>({});
  const [attestConfirmed, setAttestConfirmed] = useState(false);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const isJunior = paid === "Junior";
  const firmId = fixedBrokerage?.id ?? firm?.brokerageId ?? null;
  const firmName = fixedBrokerage?.name ?? firm?.brokerageName ?? "the brokerage";

  // Outstanding deals feed the absorb picklist — only relevant for a Junior
  // conversion (Independent never absorbs). Disabled otherwise so we never
  // fetch, and zeroed for Independent so a stale cache can't surface deals.
  const dealsQuery = useBrokerOutstandingDeals(brokerId, isJunior);
  const deals = useMemo(
    () => (isJunior ? dealsQuery.data ?? [] : []),
    [isJunior, dealsQuery.data],
  );

  // Seed the per-deal decision to the safe default (Keep paid direct); an
  // already-absorbed deal is locked to Absorb.
  useEffect(() => {
    if (!dealsQuery.data) return;
    const next: Record<string, boolean> = {};
    for (const d of dealsQuery.data) next[d.dealId] = d.isAbsorbed;
    setDecisions(next);
  }, [dealsQuery.data]);

  // A deal newly marked Absorb (not already absorbed) is what triggers the
  // attestation gate.
  const newAbsorbCount = deals.filter(
    (d) => !d.isAbsorbed && decisions[d.dealId],
  ).length;
  const requiresAttestation = newAbsorbCount > 0;

  const convert = useConvertBroker(brokerId);

  const canConfirm =
    !!firmId && (!requiresAttestation || attestConfirmed) && !convert.isPending;

  function confirm() {
    if (!firmId) {
      setError("Select a covering brokerage.");
      return;
    }
    setError(null);
    const body: BrokerConversionRequest = {
      requestedType: paidToType[paid],
      coveringBrokerageId: firmId,
      dealDecisions:
        isJunior && deals.length > 0
          ? deals.map((d) => ({ dealId: d.dealId, absorb: !!decisions[d.dealId] }))
          : undefined,
      attestation: requiresAttestation
        ? { confirmed: attestConfirmed, note: note.trim() || null }
        : undefined,
    };
    convert.mutate(body, {
      onSuccess: onConverted,
      onError: (e: unknown) =>
        setError(e instanceof Error ? e.message : "Conversion failed."),
    });
  }

  const paidOptions: { value: PaidAs; label: string; help: string }[] = [
    {
      value: "Junior",
      label: "Junior broker",
      help: `Phoenix pays ${firmName}; the brokerage pays the broker off-platform.`,
    },
    {
      value: "Independent",
      label: "Independent affiliated",
      help: "Paid directly by Phoenix; brokerage attached for tracking only.",
    },
  ];

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Link broker to brokerage"
      size="md"
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={convert.isPending}>
            Cancel
          </Button>
          <Button size="sm" loading={convert.isPending} disabled={!canConfirm} onClick={confirm}>
            Confirm
          </Button>
        </div>
      }
    >
      <div className="grid gap-space-4">
        {/* Q1 — how paid */}
        <div>
          <h3 className="font-display font-bold text-h5 text-onyx mb-space-2">
            How will {brokerName} be paid under {firmName}?
          </h3>
          <div className="grid gap-space-2" role="radiogroup" aria-label="How the broker is paid">
            {paidOptions.map((o) => (
              <button
                key={o.value}
                type="button"
                role="radio"
                aria-checked={paid === o.value}
                disabled={convert.isPending}
                onClick={() => setPaid(o.value)}
                className={cn(
                  "text-left px-space-3 py-space-2 rounded-md border transition-colors",
                  paid === o.value
                    ? "border-gold bg-white-warm text-onyx shadow-goldRing"
                    : "border-border-light bg-white-pure text-onyx hover:border-gold",
                )}
              >
                <span className="block text-small font-semibold">{o.label}</span>
                <span className="block text-caption text-gray-mid mt-[2px]">{o.help}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Q1b — covering firm */}
        {fixedBrokerage ? (
          <FormField label="Covering brokerage">
            <div className="text-small font-semibold text-onyx border border-border-light rounded-md p-space-2 bg-white-pure">
              {fixedBrokerage.name}
            </div>
          </FormField>
        ) : (
          <FormField
            label={`Covering brokerage${isJunior ? " (required)" : " (optional)"}`}
            required={isJunior}
            helperText={firm ? `Selected: ${firm.brokerageName}` : undefined}
          >
            {firm ? (
              <div className="flex items-center justify-between border border-border-light rounded-md p-space-2 bg-white-pure">
                <span className="text-small font-semibold text-onyx">{firm.brokerageName}</span>
                <Button
                  variant="text"
                  size="sm"
                  onClick={() => setFirm(null)}
                  disabled={convert.isPending}
                >
                  Change
                </Button>
              </div>
            ) : (
              <BrokeragePicker
                onSelect={setFirm}
                placeholder="Search or create a brokerage…"
                disabled={convert.isPending}
              />
            )}
          </FormField>
        )}

        {/* Q2 — per-deal absorb (Junior with outstanding deals only) */}
        {isJunior && dealsQuery.isLoading && (
          <p className="text-caption text-gray-mid">Checking outstanding deals…</p>
        )}
        {isJunior && deals.length > 0 && (
          <div>
            <h3 className="font-display font-bold text-h5 text-onyx mb-space-2">
              What happens to {brokerName}'s current deals?
            </h3>
            <ul className="grid gap-space-2">
              {deals.map((d) => {
                const absorb = !!decisions[d.dealId];
                return (
                  <li
                    key={d.dealId}
                    className="border border-border-light rounded-md p-space-2 flex items-start justify-between gap-space-3"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-space-2 flex-wrap">
                        <span className="text-small font-semibold text-onyx">
                          #{d.dealNumber}
                        </span>
                        <span className="text-caption text-gray-mid">{d.stage}</span>
                        {d.isFunded && <Badge status="info">Funded</Badge>}
                        <span className="text-caption text-gray-mid">
                          Paid: {d.currentPayee === "Brokerage" ? firmName : brokerName}
                        </span>
                      </div>
                    </div>
                    {d.isAbsorbed ? (
                      <Badge status="gold">Absorbed</Badge>
                    ) : (
                      <div
                        className="flex items-center gap-space-1 shrink-0"
                        role="radiogroup"
                        aria-label={`Deal ${d.dealNumber} decision`}
                      >
                        <DealChoice
                          selected={!absorb}
                          disabled={convert.isPending}
                          onClick={() =>
                            setDecisions((p) => ({ ...p, [d.dealId]: false }))
                          }
                        >
                          Keep paid direct
                        </DealChoice>
                        <DealChoice
                          selected={absorb}
                          disabled={convert.isPending}
                          onClick={() =>
                            setDecisions((p) => ({ ...p, [d.dealId]: true }))
                          }
                        >
                          Absorb
                        </DealChoice>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Attestation — gates confirm whenever ≥1 deal is newly marked Absorb */}
        {requiresAttestation && (
          <div className="grid gap-space-2">
            <p className="text-caption text-status-error flex gap-space-1" role="alert">
              <AlertTriangle size={14} strokeWidth={1.5} aria-hidden className="shrink-0 mt-[2px]" />
              <span>
                ⚠ Absorbing is permanent and cannot be undone. From today forward,{" "}
                {firmName} — not {brokerName} — will be paid on each absorbed deal.
                Payments already made are unaffected. This can have serious financial
                consequences for the broker. Do not proceed without written authorization
                from the broker.
              </span>
            </p>
            <label className="flex items-start gap-space-2 text-small text-onyx">
              <input
                type="checkbox"
                className="mt-[3px]"
                checked={attestConfirmed}
                disabled={convert.isPending}
                onChange={(e) => setAttestConfirmed(e.target.checked)}
              />
              <span>
                I confirm I have the broker's written authorization to absorb the selected
                deal(s) into {firmName}, and it is on file.
              </span>
            </label>
            <FormField label="Reference / note (optional)">
              <Textarea
                rows={2}
                value={note}
                disabled={convert.isPending}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Where the authorization is filed, etc."
              />
            </FormField>
          </div>
        )}

        {error && (
          <p className="text-label text-status-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

function DealChoice({
  selected,
  disabled,
  onClick,
  children,
}: {
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "px-space-2 py-space-1 rounded-md border text-caption font-semibold transition-colors",
        selected
          ? "border-gold bg-white-warm text-onyx"
          : "border-border-light bg-white-pure text-gray-mid hover:border-gold",
      )}
    >
      {children}
    </button>
  );
}
